import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import Navbar from "@/components/layout/Navbar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { useGeneration } from "@/hooks/useGeneration";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
    Sparkles,
    Upload,
    Play,
    Trash2,
    FileText,
    Clock,
    Loader2,
    Download,
    RefreshCw,
    ChevronLeft,
    ChevronRight,
    History as HistoryIcon,
    Video,
    Image as ImageIcon
} from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";

interface BulkJob {
    id: string;
    model_name: string;
    ratio: string;
    status: string;
    total_items: number;
    completed_items: number;
    failed_items: number;
    created_at: string;
}

const GrokBulk = () => {
    const { t } = useLanguage();
    const { user } = useAuth();
    const { userCredits } = useGeneration();
    const { toast } = useToast();

    const [prompts, setPrompts] = useState<string[]>([]);
    const [pasteText, setPasteText] = useState("");
    const [selectedGrokModel, setSelectedGrokModel] = useState("grok-text-to-vdo-10s");
    const [selectedRatio, setSelectedRatio] = useState("9:16");
    const [selectedMode, setSelectedMode] = useState("normal");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [recentJobs, setRecentJobs] = useState<BulkJob[]>([]);
    const [totalJobsCount, setTotalJobsCount] = useState(0);
    const [jobsPage, setJobsPage] = useState(1);
    const JOBS_PER_PAGE = 10;
    const [downloadingJobId, setDownloadingJobId] = useState<string | null>(null);
    const [downloadProgress, setDownloadProgress] = useState<{ current: number; total: number } | null>(null);

    const grokModels = [
        { id: "grok-text-to-vdo-10s", name: "Grok Text to Video (10s)", credits: 30, duration: "10s", type: "video" },
        { id: "grok-text-to-vdo-6s", name: "Grok Text to Video (6s)", credits: 18, duration: "6s", type: "video" },
        { id: "grok-text-to-vdo-15s", name: "Grok Text to Video (15s)", credits: 45, duration: "15s", type: "video" },
        { id: "grok-text-to-vdo-20s", name: "Grok Text to Video (20s)", credits: 60, duration: "20s", type: "video" },
        { id: "grok-text-to-vdo-25s", name: "Grok Text to Video (25s)", credits: 75, duration: "25s", type: "video" },
        { id: "grok-text-to-vdo-30s", name: "Grok Text to Video (30s)", credits: 90, duration: "30s", type: "video" },
        { id: "grok-text-to-img", name: "Grok Text to Image", credits: 5, duration: "-", type: "photo" },
    ];

    const aspectOptions = [
        { value: "9:16", label: t("แนวตั้ง (9:16)", "Portrait (9:16)") },
        { value: "16:9", label: t("แนวนอน (16:9)", "Landscape (16:9)") },
        { value: "2:3", label: "2:3" },
        { value: "3:2", label: "3:2" },
        { value: "1:1", label: "1:1" },
    ];

    const modeOptions = ["normal", "fun", "spicy"];

    const selectedModelInfo = grokModels.find(m => m.id === selectedGrokModel);
    const isVideoModel = selectedModelInfo?.type === "video";

    useEffect(() => {
        if (user) {
            fetchRecentJobs();
        }
    }, [user]);

    const fetchRecentJobs = async (page: number = jobsPage) => {
        if (!user) return;

        // Get total count - filter by grok models
        const { count } = await supabase
            .from("bulk_video_jobs" as any)
            .select("*", { count: "exact", head: true })
            .ilike("model_id", "grok-%");

        setTotalJobsCount(count || 0);

        const from = (page - 1) * JOBS_PER_PAGE;
        const to = from + JOBS_PER_PAGE - 1;

        const { data, error } = await supabase
            .from("bulk_video_jobs" as any)
            .select("*")
            .ilike("model_id", "grok-%")
            .order("created_at", { ascending: false })
            .range(from, to);

        if (!error && data) {
            const processedJobs = await Promise.all((data as any[]).map(async (job) => {
                if (job.status === 'processing' && job.completed_items === 0 && job.failed_items === 0) {
                    const { data: items } = await supabase
                        .from("bulk_video_items" as any)
                        .select("status")
                        .eq("job_id", job.id);

                    if (items && items.length > 0) {
                        const completed = items.filter((i: any) => i.status === "completed").length;
                        const failed = items.filter((i: any) => i.status === "failed").length;

                        if (completed > 0 || failed > 0) {
                            supabase
                                .from("bulk_video_jobs" as any)
                                .update({
                                    completed_items: completed,
                                    failed_items: failed,
                                    status: (completed + failed >= job.total_items) ? "completed" : "processing",
                                    updated_at: new Date().toISOString()
                                })
                                .eq("id", job.id)
                                .then(() => {});

                            return { ...job, completed_items: completed, failed_items: failed };
                        }
                    }
                }
                return job;
            }));

            setRecentJobs(processedJobs);
        }
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result as string;
            let extractedPrompts: string[] = [];

            if (file.name.toLowerCase().endsWith(".csv")) {
                const lines = content.split("\n").map(line => line.trim()).filter(line => line.length > 0);
                extractedPrompts = lines.map(line => {
                    if (line.startsWith('"')) {
                        const endQuote = line.indexOf('"', 1);
                        if (endQuote > 0) {
                            return line.substring(1, endQuote).trim();
                        }
                    }
                    const commaIndex = line.indexOf(',');
                    return commaIndex > 0 ? line.substring(0, commaIndex).trim() : line.trim();
                }).filter(p => p.length > 0);

                if (extractedPrompts.length > 1) {
                    const firstLine = extractedPrompts[0].toLowerCase();
                    if (firstLine === "prompt" || firstLine === "prompts" || firstLine === "text" || firstLine.length < 5) {
                        extractedPrompts = extractedPrompts.slice(1);
                    }
                }
            } else {
                extractedPrompts = content
                    .split("\n")
                    .map(line => line.trim())
                    .filter(line => line.length > 0);
            }

            extractedPrompts = [...new Set(extractedPrompts)];

            setPrompts(extractedPrompts);
            setPasteText(extractedPrompts.join("\n"));

            toast({
                title: t("นำเข้าสำเร็จ", "Import successful"),
                description: t(`นำเข้า ${extractedPrompts.length} รายการพรอมต์แล้ว`, `Imported ${extractedPrompts.length} prompts`),
            });

            e.target.value = "";
        };
        reader.readAsText(file);
    };

    const handlePasteTextChange = (value: string) => {
        setPasteText(value);
        const extractedPrompts = value
            .split("\n")
            .map(line => line.trim())
            .filter(line => line.length > 0);
        const uniquePrompts = [...new Set(extractedPrompts)];
        setPrompts(uniquePrompts);
    };

    const getRequiredCredits = () => {
        const model = grokModels.find(m => m.id === selectedGrokModel);
        return (model?.credits || 0) * (prompts.length || 0);
    };

    const handleSubmitBulk = async () => {
        if (!user || prompts.length === 0) return;

        const totalRequired = getRequiredCredits();
        if ((userCredits || 0) < totalRequired) {
            toast({
                variant: "destructive",
                title: t("เครดิตไม่พอ", "Insufficient credits"),
                description: t("กรุณาเติมเครดิตก่อนเริ่มงานแบบ Bulk", "Please buy more credits before starting bulk job"),
            });
            return;
        }

        setIsSubmitting(true);
        const modelInfo = grokModels.find(m => m.id === selectedGrokModel);

        try {
            const { data: job, error: jobError } = await supabase
                .from("bulk_video_jobs" as any)
                .insert({
                    user_id: user.id,
                    model_id: selectedGrokModel,
                    model_name: isVideoModel ? `${modelInfo?.name || ""} (${selectedMode})` : (modelInfo?.name || ""),
                    ratio: selectedRatio,
                    total_items: prompts.length,
                    status: 'pending'
                })
                .select()
                .single();

            if (jobError) throw jobError;

            const items = prompts.map(p => ({
                job_id: (job as any).id,
                user_id: user.id,
                prompt: p,
                status: 'pending'
            }));

            const { error: itemsError } = await supabase
                .from("bulk_video_items" as any)
                .insert(items);

            if (itemsError) throw itemsError;

            setPrompts([]);
            setPasteText("");

            toast({
                title: t("ส่งงานสำเร็จ", "Bulk job submitted"),
                description: t("ระบบกำลังเริ่มประมวลผล คุณสามารถปิดหน้านี้ได้ทันที", "Job is being processed. You can close this page now."),
            });

            await triggerBulkProcessing();
            fetchRecentJobs();

        } catch (error: any) {
            console.error("Bulk submission error:", error);
            toast({
                variant: "destructive",
                title: t("เกิดข้อผิดพลาด", "Error occurred"),
                description: error.message,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClearQueue = () => {
        setPrompts([]);
        setPasteText("");
    };

    const triggerBulkProcessing = async () => {
        try {
            const session = await supabase.auth.getSession();
            const token = session.data.session?.access_token;
            if (!token) return;

            const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-bulk-video`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error("Bulk processing trigger failed:", errorText);
            } else {
                const result = await response.json();
                console.log("Bulk processing triggered:", result);
            }
        } catch (e) {
            console.error("Bulk processing trigger error:", e);
        }
    };

    const handleRetryProcessing = async (jobId: string) => {
        toast({
            title: t("กำลังเริ่มประมวลผลใหม่...", "Retrying processing..."),
        });
        await triggerBulkProcessing();
        setTimeout(() => fetchRecentJobs(), 2000);
    };

    const handleRefreshJobStatus = async (jobId: string) => {
        try {
            const { data: items, error } = await supabase
                .from("bulk_video_items" as any)
                .select("status")
                .eq("job_id", jobId);

            if (error) throw error;

            const completed = items?.filter((i: any) => i.status === "completed").length || 0;
            const failed = items?.filter((i: any) => i.status === "failed").length || 0;
            const total = items?.length || 0;

            const newStatus = (completed + failed >= total && total > 0) ? "completed" : "processing";

            await supabase
                .from("bulk_video_jobs" as any)
                .update({
                    completed_items: completed,
                    failed_items: failed,
                    status: newStatus,
                    updated_at: new Date().toISOString()
                })
                .eq("id", jobId);

            toast({
                title: t("อัพเดทสถานะสำเร็จ", "Status updated"),
                description: `${completed}/${total} ${t("เสร็จสมบูรณ์", "completed")}`,
            });

            fetchRecentJobs();
        } catch (error: any) {
            console.error("Refresh status error:", error);
            toast({
                variant: "destructive",
                title: t("อัพเดทสถานะผิดพลาด", "Status update failed"),
                description: error.message,
            });
        }
    };

    const handleDownloadAll = async (jobId: string, jobName?: string) => {
        setDownloadingJobId(jobId);
        setDownloadProgress({ current: 0, total: 0 });

        try {
            const { data, error } = await supabase
                .from("bulk_video_items" as any)
                .select("log_id, prompt")
                .eq("job_id", jobId)
                .eq("status", "completed");

            if (error) throw error;
            if (!data || data.length === 0) {
                toast({
                    title: t("ไม่พบไฟล์เสร็จสมบูรณ์", "No completed files found"),
                    description: t("ยังไม่มีงานที่เสร็จสมบูรณ์ในชุดนี้", "There are no completed items in this batch yet."),
                });
                return;
            }

            const logIds = (data as any[]).map(item => item.log_id).filter(Boolean);
            const promptMap = new Map((data as any[]).map(item => [item.log_id, item.prompt]));

            const { data: logs, error: logsError } = await supabase
                .from("generation_logs")
                .select("id, result_url, model, prompt")
                .in("id", logIds as any);

            if (logsError) throw logsError;

            const fileData = logs?.filter(log => log.result_url && log.result_url.startsWith("http")).map(log => ({
                url: log.result_url,
                prompt: promptMap.get(log.id) || log.prompt || "file"
            })) || [];

            if (fileData.length === 0) {
                toast({
                    title: t("ไม่พบลิงก์ดาวน์โหลด", "No download links found"),
                });
                return;
            }

            setDownloadProgress({ current: 0, total: fileData.length });

            toast({
                title: t(`กำลังดาวน์โหลด ${fileData.length} ไฟล์`, `Downloading ${fileData.length} files`),
                description: t("กำลังสร้างไฟล์ ZIP กรุณารอสักครู่...", "Creating ZIP file, please wait..."),
            });

            const zip = new JSZip();
            const folder = zip.folder("grok_results") || zip;

            // Determine file extension based on model
            const isImageModel = selectedGrokModel.includes("img") || jobName?.toLowerCase().includes("image");
            const ext = isImageModel ? "png" : "mp4";

            for (let i = 0; i < fileData.length; i++) {
                const { url, prompt } = fileData[i];
                setDownloadProgress({ current: i + 1, total: fileData.length });

                try {
                    const response = await fetch(url);
                    if (!response.ok) throw new Error(`HTTP ${response.status}`);

                    const blob = await response.blob();

                    const safePrompt = prompt
                        .replace(/[^a-zA-Z0-9ก-๙\s]/g, '')
                        .trim()
                        .substring(0, 30)
                        .trim() || `file`;
                    const filename = `${String(i + 1).padStart(3, '0')}_${safePrompt}.${ext}`;

                    folder.file(filename, blob);
                } catch (fetchErr) {
                    console.warn(`Failed to download file ${i + 1}:`, fetchErr);
                }
            }

            toast({
                title: t("กำลังสร้างไฟล์ ZIP...", "Creating ZIP file..."),
            });

            const zipBlob = await zip.generateAsync({
                type: "blob",
                compression: "DEFLATE",
                compressionOptions: { level: 1 }
            });

            const date = new Date().toISOString().split('T')[0];
            const zipFilename = `grok_bulk_${jobName || jobId.substring(0, 8)}_${date}.zip`;

            saveAs(zipBlob, zipFilename);

            toast({
                title: t("ดาวน์โหลดสำเร็จ!", "Download complete!"),
                description: t(`บันทึก ${fileData.length} ไฟล์เป็นไฟล์ ZIP`, `Saved ${fileData.length} files as ZIP file`),
            });

        } catch (error: any) {
            console.error("Download error:", error);
            toast({
                variant: "destructive",
                title: t("ดาวน์โหลดผิดพลาด", "Download failed"),
                description: error.message,
            });
        } finally {
            setDownloadingJobId(null);
            setDownloadProgress(null);
        }
    };

    return (
        <div className="min-h-screen bg-background">
            <Navbar />

            <main className="container mx-auto px-4 py-8 mt-16 md:mt-20">
                <div className="flex flex-col gap-6 max-w-5xl mx-auto">
                    {/* Header */}
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-heading font-bold flex items-center gap-2">
                                <Sparkles className="h-8 w-8 text-primary" />
                                {t("Bulk Grok Imagine", "Bulk Grok Imagine")}
                            </h1>
                            <p className="text-muted-foreground mt-1">
                                {t("ส่งงาน Grok แบบ Bulk แล้วปิดหน้าเว็บได้เลย ระบบจะทยอยทำให้อัตโนมัติ", "Submit Grok bulk jobs and close the page. System processes them automatically.")}
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <Button variant="outline" asChild>
                                <Link to="/generate/grok">
                                    <Sparkles className="h-4 w-4 mr-2" />
                                    {t("Grok ปกติ", "Normal Grok")}
                                </Link>
                            </Button>
                            <Button variant="outline" asChild>
                                <Link to="/history">
                                    <HistoryIcon className="h-4 w-4 mr-2" />
                                    {t("ประวัติทั้งหมด", "Full History")}
                                </Link>
                            </Button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Left: Configuration */}
                        <div className="lg:col-span-1 space-y-6">
                            <Card variant="glass">
                                <CardHeader>
                                    <CardTitle className="text-lg">{t("ตั้งค่ารุ่นและขนาด", "Model & Settings")}</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">{t("เลือกรุ่น (Model)", "Select Model")}</label>
                                        <Select value={selectedGrokModel} onValueChange={setSelectedGrokModel}>
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {grokModels.map(model => (
                                                    <SelectItem key={model.id} value={model.id}>
                                                        <div className="flex justify-between w-full gap-4">
                                                            <span>{model.name}</span>
                                                            <span className="text-xs text-muted-foreground">{model.credits} Cr.</span>
                                                        </div>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">{t("อัตราส่วน (Aspect Ratio)", "Aspect Ratio")}</label>
                                        <Select value={selectedRatio} onValueChange={setSelectedRatio}>
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {aspectOptions.map(opt => (
                                                    <SelectItem key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {isVideoModel && (
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium">{t("โหมด (Mode)", "Mode")}</label>
                                            <div className="grid grid-cols-3 gap-2">
                                                {modeOptions.map((m) => (
                                                    <Button
                                                        key={m}
                                                        variant={selectedMode === m ? "default" : "outline"}
                                                        onClick={() => setSelectedMode(m)}
                                                        className="capitalize text-xs"
                                                        size="sm"
                                                    >
                                                        {m}
                                                    </Button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            <Card variant="glass">
                                <CardHeader>
                                    <CardTitle className="text-lg">{t("นำเข้าข้อมูล", "Import Data")}</CardTitle>
                                    <CardDescription className="text-xs">
                                        {t("รองรับไฟล์ .txt หรือ .csv (1 พรอมต์ต่อ 1 บรรทัด)", "Supports .txt or .csv files (1 prompt per line)")}
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">{t("วางพรอมต์ (1 บรรทัดต่อ 1 รายการ)", "Paste prompts (1 per line)")}</label>
                                        <Textarea
                                            placeholder={t("วางพรอมต์ที่นี่...\nบรรทัดที่ 1\nบรรทัดที่ 2\nบรรทัดที่ 3", "Paste prompts here...\nLine 1\nLine 2\nLine 3")}
                                            value={pasteText}
                                            onChange={(e) => handlePasteTextChange(e.target.value)}
                                            className="min-h-[120px] text-sm resize-none"
                                        />
                                    </div>

                                    <div className="flex items-center gap-3">
                                        <div className="flex-1 h-px bg-border" />
                                        <span className="text-xs text-muted-foreground">{t("หรืออัปโหลดไฟล์", "or upload file")}</span>
                                        <div className="flex-1 h-px bg-border" />
                                    </div>

                                    <div className="relative group h-24">
                                        <Input
                                            type="file"
                                            accept=".txt,.csv"
                                            onChange={handleFileUpload}
                                            className="absolute inset-0 opacity-0 cursor-pointer z-10 h-full w-full"
                                        />
                                        <div className="absolute inset-0 border-2 border-dashed border-border group-hover:border-primary/50 transition-colors rounded-lg flex flex-col items-center justify-center text-muted-foreground bg-muted/5 pointer-events-none">
                                            <Upload className="h-6 w-6 mb-1" />
                                            <p className="text-xs text-center px-4">{t("คลิกเพื่ออัปโหลดไฟล์", "Click to upload file")}</p>
                                            <p className="text-[10px] text-center px-4 mt-0.5 opacity-60">.txt, .csv</p>
                                        </div>
                                    </div>

                                    {prompts.length > 0 && (
                                        <div className="space-y-4 pt-2">
                                            <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 space-y-2">
                                                <div className="flex justify-between text-xs text-muted-foreground uppercase font-bold tracking-wider">
                                                    <span>{t("เตรียมส่ง", "Ready to submit")}</span>
                                                    <span>{prompts.length} {t("รายการ", "items")}</span>
                                                </div>
                                                <div className="flex justify-between items-end">
                                                    <div className="text-sm">
                                                        <p className="text-xs text-muted-foreground">{t("ใช้เครดิตทั้งหมด", "Total credits needed")}:</p>
                                                        <p className="font-bold text-lg text-primary">{getRequiredCredits().toLocaleString()} Cr.</p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="border border-border/50 rounded-lg overflow-hidden">
                                                <div className="bg-muted/30 px-3 py-2 border-b border-border/50">
                                                    <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                                                        {t("รายการพรอมต์ทั้งหมด", "All Prompts Preview")}
                                                    </p>
                                                </div>
                                                <div className="max-h-[200px] overflow-y-auto">
                                                    {prompts.map((prompt, index) => (
                                                        <div
                                                            key={index}
                                                            className="px-3 py-2 border-b border-border/30 last:border-b-0 hover:bg-muted/20 transition-colors"
                                                        >
                                                            <div className="flex items-start gap-2">
                                                                <span className="text-[10px] font-bold text-primary bg-primary/10 rounded px-1.5 py-0.5 shrink-0">
                                                                    {index + 1}
                                                                </span>
                                                                <p className="text-xs text-foreground/80 line-clamp-2 break-all">
                                                                    {prompt}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            <Button
                                                className="w-full h-12 gap-2 text-lg shadow-glow"
                                                variant="glow"
                                                onClick={handleSubmitBulk}
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Play className="h-5 w-5" />}
                                                {t("ยืนยันส่งงาน Grok Bulk", "Confirm Grok Bulk Order")}
                                            </Button>

                                            <Button variant="ghost" size="sm" className="w-full text-destructive" onClick={handleClearQueue}>
                                                <Trash2 className="h-4 w-4 mr-2" />
                                                {t("ล้างรายการ", "Clear items")}
                                            </Button>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>

                        {/* Right: History & Progress */}
                        <div className="lg:col-span-2 space-y-6">
                            <Card variant="glass" className="min-h-[500px]">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Clock className="h-5 w-5 text-primary" />
                                        {t("สถานะงาน Grok Bulk", "Grok Bulk Jobs")}
                                    </CardTitle>
                                    <CardDescription>
                                        {t("งานจะทยอยเสร็จและไปอยู่ในหน้าประวัติหลัก", "Jobs process over time and results appear in History")}
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-4">
                                        {recentJobs.length === 0 ? (
                                            <div className="flex flex-col items-center justify-center py-20 text-muted-foreground opacity-50">
                                                <FileText className="h-16 w-16 mb-4" />
                                                <p>{t("ยังไม่มีรายการส่งงาน Grok", "No Grok bulk jobs yet")}</p>
                                            </div>
                                        ) : (
                                            recentJobs.map((job) => (
                                                <div key={job.id} className="p-4 rounded-xl border border-border bg-card/50 space-y-3">
                                                    <div className="flex items-center justify-between">
                                                        <div className="space-y-1">
                                                            <div className="flex items-center gap-2">
                                                                <p className="font-bold text-sm">{job.model_name}</p>
                                                                <Badge variant="outline" className="text-[10px] py-0">{job.ratio}</Badge>
                                                            </div>
                                                            <p className="text-[10px] text-muted-foreground">{new Date(job.created_at).toLocaleString()}</p>
                                                        </div>
                                                        <Badge variant={job.status === 'completed' ? 'secondary' : (job.status === 'processing' ? 'secondary' : 'outline')} className={job.status === 'completed' ? 'bg-green-500/10 text-green-500 border-green-500/20' : ''}>
                                                            {job.status === 'processing' && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                                                            {job.status}
                                                        </Badge>
                                                    </div>

                                                    <div className="space-y-1">
                                                        <div className="flex justify-between text-xs mb-1">
                                                            <span>{t("ความคืบหน้า", "Progress")}</span>
                                                            <span className="font-bold">
                                                                {job.completed_items + job.failed_items} / {job.total_items}
                                                            </span>
                                                        </div>
                                                        <Progress value={((job.completed_items + job.failed_items) / job.total_items) * 100} className="h-1.5" />
                                                    </div>

                                                    <div className="flex justify-end gap-2 pt-1">
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            className="h-8 text-xs gap-1 text-muted-foreground"
                                                            onClick={() => handleRefreshJobStatus(job.id)}
                                                        >
                                                            <RefreshCw className="h-3.5 w-3.5" />
                                                        </Button>
                                                        {(job.status === 'pending' || job.status === 'processing') && job.completed_items + job.failed_items < job.total_items && (
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="h-8 text-xs gap-2"
                                                                onClick={() => handleRetryProcessing(job.id)}
                                                            >
                                                                <RefreshCw className="h-3.5 w-3.5" />
                                                                {t("เริ่มประมวลผล", "Start Processing")}
                                                            </Button>
                                                        )}
                                                        {job.completed_items > 0 && (
                                                            <Button
                                                                size="sm"
                                                                variant="glow"
                                                                className="h-8 text-xs gap-2"
                                                                onClick={() => handleDownloadAll(job.id, job.model_name)}
                                                                disabled={downloadingJobId === job.id}
                                                            >
                                                                {downloadingJobId === job.id ? (
                                                                    <>
                                                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                                        {downloadProgress
                                                                            ? `${downloadProgress.current}/${downloadProgress.total}`
                                                                            : t("กำลังโหลด...", "Loading...")}
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Download className="h-3.5 w-3.5" />
                                                                        {t("ดาวน์โหลด ZIP", "Download ZIP")} ({job.completed_items})
                                                                    </>
                                                                )}
                                                            </Button>
                                                        )}
                                                        <Button size="sm" variant="outline" className="h-8 text-xs" asChild>
                                                            <Link to="/history">
                                                                <HistoryIcon className="h-3.5 w-3.5 mr-2" />
                                                                {t("ดูในประวัติ", "View in History")}
                                                            </Link>
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))
                                        )}

                                        {/* Pagination Controls */}
                                        {totalJobsCount > JOBS_PER_PAGE && (
                                            <div className="flex items-center justify-between pt-4 border-t border-border/50">
                                                <p className="text-xs text-muted-foreground">
                                                    {t("แสดง", "Showing")} {((jobsPage - 1) * JOBS_PER_PAGE) + 1}-{Math.min(jobsPage * JOBS_PER_PAGE, totalJobsCount)} {t("จาก", "of")} {totalJobsCount}
                                                </p>
                                                <div className="flex items-center gap-2">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-8 w-8 p-0"
                                                        onClick={() => {
                                                            const newPage = jobsPage - 1;
                                                            setJobsPage(newPage);
                                                            fetchRecentJobs(newPage);
                                                        }}
                                                        disabled={jobsPage <= 1}
                                                    >
                                                        <ChevronLeft className="h-4 w-4" />
                                                    </Button>
                                                    <span className="text-sm font-medium px-2">
                                                        {jobsPage} / {Math.ceil(totalJobsCount / JOBS_PER_PAGE)}
                                                    </span>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-8 w-8 p-0"
                                                        onClick={() => {
                                                            const newPage = jobsPage + 1;
                                                            setJobsPage(newPage);
                                                            fetchRecentJobs(newPage);
                                                        }}
                                                        disabled={jobsPage >= Math.ceil(totalJobsCount / JOBS_PER_PAGE)}
                                                    >
                                                        <ChevronRight className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default GrokBulk;
